import { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";
import { setMyAccess } from "../lib/menuRegistry";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuAccessLoaded, setMenuAccessLoaded] = useState(false);

  // Load the current user's effective menu access into the module cache BEFORE guarded routes
  // evaluate it, so grants/revocations apply on direct URL loads and refreshes (not just in-app nav).
  const reloadMenuAccess = () => {
    return api.get("/menu-access/me")
      .then((r) => { setMyAccess(r.data); })
      .catch(() => { setMyAccess(null); })
      .finally(() => setMenuAccessLoaded(true));
  };
  useEffect(() => {
    if (!user) { setMyAccess(null); setMenuAccessLoaded(false); return; }
    setMenuAccessLoaded(false);
    reloadMenuAccess();
  }, [user]);

  useEffect(() => {
    const token = localStorage.getItem("jde_token");
    if (!token) {
      // PREVIEW ONLY: skip the login screen. On the preview host, ask the backend for a
      // session (backend gates this on a preview-only env flag; production returns 403 and
      // the normal login screen shows). Any failure falls back to the login screen.
      // TEMP SSO-TEST BYPASS: opening the app with ?login=1 skips preview auto-login so the
      // login screen + "Sign in with Microsoft" button are reachable for testing.
      const wantsLogin = new URLSearchParams(window.location.search).get("login") === "1";
      const isPreview = window.location.hostname.includes("preview.emergentagent.com");
      if (isPreview && !wantsLogin) {
        api.post("/auth/preview-autologin")
          .then((r) => {
            localStorage.setItem("jde_token", r.data.token);
            setUser(r.data.user);
          })
          .catch(() => {})
          .finally(() => setLoading(false));
        return;
      }
      setLoading(false);
      return;
    }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch((e) => {
        // Only clear the token when it is genuinely rejected (401). A transient
        // error (network blip, 502/timeout) must NOT log the user out on mount.
        if (e?.response?.status === 401) localStorage.removeItem("jde_token");
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (username, password) => {
    const r = await api.post("/auth/login", { username, password });
    localStorage.setItem("jde_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const setPasswordWithPin = async (username, pin, newPassword) => {
    const r = await api.post("/auth/set-password", { username, pin, new_password: newPassword });
    localStorage.setItem("jde_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = () => {
    localStorage.removeItem("jde_token");
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthCtx.Provider value={{ user, loading, menuAccessLoaded, reloadMenuAccess, login, setPasswordWithPin, logout }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
